import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Alert, Button, Checkbox, DatePicker, Form, Input, Modal, Radio, Select, TimePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

// Actions
import { clear_cycles_errors, cycle_create, cycle_extend, cycle_update, get_cycle_info, load_single_cycle } from '../../redux/actions/cyclesActions';
import Axios from 'axios';
import i18next from 'i18next';

// Utils
import isEmpty from '../../utils/isEmpty';
import { area_type_options, cycle_scope_options } from '../../utils/select_options';
import { create_select_options, create_select_options_from_array } from '../../utils/create_select_options';
import { get_depots, get_groups, get_regions, get_zones } from '../../redux/actions/areasActions';
import { get_routes } from '../../redux/actions/routesActions';
import useOrgTimezone from '../../hooks/useOrgTimezone';
import {
	epoch_to_org_dayjs,
	is_default_day_end,
	is_default_day_start,
	org_date_time_to_epoch,
	org_day_end,
	org_day_start,
	time_of_day
} from '../../utils/timezone_handler';

// Capture format preference of the time inputs, it is not part of the cycle data
const TIME_FORMAT_STORAGE_KEY = 'cycle_time_format';
const TIME_FORMAT_12H = '12h';
const TIME_FORMAT_24H = '24h';

const CyclesForm = (props) => {
	const { messageApi, isModalOpen, setIsModalOpen, orgFilters, cycleAction, cycleToUpdate, setCycleToUpdate, pageSize, skip, onCycleCreated } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { regions, zones, depots, groups } = useSelector(state => state.areas);

	const { routes } = useSelector(state => state.routes);

	const { cycle_create_loading, cycle_update_loading, cycles_errors } = useSelector(state => state.cycles);
	
	const [form] = Form.useForm();
	const [areaType, setAreaType] = useState('');
	const [showReferenceIput, setShowReferenceIput] = useState(false);
	const [areaTypesOptions, setAreaTypesOptions] = useState([]);
	const [cycleScopeOptions, setCycleScopeOptions] = useState([]);
	const [referenceOptions, setReferenceOptions] = useState([]);
	const [useCustomTime, setUseCustomTime] = useState(false);
	const [timeFormat, setTimeFormat] = useState(() => localStorage.getItem(TIME_FORMAT_STORAGE_KEY) || TIME_FORMAT_12H);

	// The cycle always uses the timezone of the selected organization, not the one of the user
	const { timezone, is_fallback: isFallbackTimezone, loading: timezoneLoading } = useOrgTimezone(orgFilters.organization);

	const is12Hours = timeFormat === TIME_FORMAT_12H;
	const timePickerFormat = is12Hours ? 'hh:mm:ss A' : 'HH:mm:ss';

	const AREA_TYPES = area_type_options();
	const CYCLE_SCOPE = cycle_scope_options();

	useEffect(() => {
		setAreaTypesOptions(create_select_options_from_array(AREA_TYPES));
		setCycleScopeOptions(create_select_options_from_array(CYCLE_SCOPE));
	}, []);

	useEffect(() => {
		if (orgFilters.organization && orgFilters.workspace) {
			dispatch(get_regions(orgFilters));
			dispatch(get_zones(orgFilters));
			dispatch(get_depots(orgFilters));
			dispatch(get_groups(orgFilters));
			dispatch(get_routes({ ...orgFilters, skip: 0, limit: 0 }));
		}
	}, [orgFilters]);

	useEffect(() => {
		if (!isEmpty(cycleToUpdate)) {
			let areaInput = null;

			if (cycleToUpdate.area_type === 2) {
				const region = regions.regions.find(region => region._id.$oid === cycleToUpdate.region?.$oid);
				areaInput = { value: region._id.$oid, label: region.name };
				setReferenceOptions(create_select_options(regions.regions, 'name'));
			}
			else if (cycleToUpdate.area_type === 3) {
				const zone = zones.zones.find(zone => zone._id.$oid === cycleToUpdate.zone?.$oid);
				areaInput = { value: zone._id.$oid, label: zone.name };
				setReferenceOptions(create_select_options(zones.zones, 'name'));
			}
			else if (cycleToUpdate.area_type === 4) {
				const depot = depots.depots.find(depot => depot._id.$oid === cycleToUpdate.depot?.$oid);
				areaInput = { value: depot._id.$oid, label: depot.name };
				setReferenceOptions(create_select_options(depots.depots, 'name'));
			}
			else if (cycleToUpdate.area_type === 5) {
				const group = groups.groups.find(group => group._id.$oid === cycleToUpdate.group?.$oid);
				areaInput = { value: group._id.$oid, label: group.name };
				setReferenceOptions(create_select_options(groups.groups, 'name'));
			}
			else if (cycleToUpdate.area_type === 6) {
				const route = routes.routes.find(route => route._id.$oid === cycleToUpdate.route?.$oid);
				areaInput = { value: route._id.$oid, label: route.name };
				setReferenceOptions(create_select_options(routes.routes, 'name'));
			}

			// The stored dates are read on the organization timezone, so the days and times shown are its own
			const startDate = epoch_to_org_dayjs(cycleToUpdate.start.$date, timezone);
			const endDate = epoch_to_org_dayjs(cycleToUpdate.end.$date, timezone);

			/*
				A cycle that does not cover the whole day has custom times, so the checkbox is
				checked and the times are preloaded, otherwise editing it would silently drop them
			*/
			const hasCustomTime = !is_default_day_start(startDate) || !is_default_day_end(endDate);

			form.setFieldsValue({
				organization: orgFilters.organization,
				workspace: orgFilters.workspace,
				module: orgFilters.module,
				area_type: cycleToUpdate.area_type,
				area: areaInput,
				name: cycleToUpdate.name,
				description: cycleToUpdate.description,
				date: [startDate, endDate],
				scope: cycleToUpdate.scope,
				start_time: startDate,
				end_time: endDate
			});

			setUseCustomTime(hasCustomTime);

			setAreaType(areaTypesOptions.find(option => option.value === form.getFieldValue('area_type'))?.label);
			cycleToUpdate.area_type === 1 ? setShowReferenceIput(false) : setShowReferenceIput(true);
		}
	}, [cycleToUpdate, timezone]);

	const handleCancel = () => {
		setIsModalOpen(false);
		setCycleToUpdate({});
		dispatch(clear_cycles_errors());
		setAreaType('');
		setShowReferenceIput(false);
		setUseCustomTime(false);
		form.resetFields();
	}

	const onCustomTimeChange = (e) => {
		const checked = e.target.checked;
		setUseCustomTime(checked);

		if (checked) {
			// The whole day is preloaded so only the time that needs to change has to be touched
			form.setFieldsValue({
				start_time: form.getFieldValue('start_time') || org_day_start(),
				end_time: form.getFieldValue('end_time') || org_day_end()
			});
		}
	}

	const onTimeFormatChange = (e) => {
		const format = e.target.value;
		setTimeFormat(format);
		localStorage.setItem(TIME_FORMAT_STORAGE_KEY, format);
	}

	/*
		On a cycle that starts and ends the same day the end time has to be strictly later than
		the start one, as equal times would create a cycle with no duration
	*/
	const validateEndTime = (_, value) => {
		const date = form.getFieldValue('date');
		const startTime = form.getFieldValue('start_time');

		if (!useCustomTime || !value || !startTime || !date?.[0] || !date?.[1]) {
			return Promise.resolve();
		}

		const isSameDay = date[0].format('YYYY-MM-DD') === date[1].format('YYYY-MM-DD');

		if (isSameDay && time_of_day(value) <= time_of_day(startTime)) {
			return Promise.reject(new Error(t('cycle.validationMsg.endBeforeStart')));
		}

		return Promise.resolve();
	}

	// Custom success handler for wizard use
	const handleWizardSuccess = (createdCycle) => {
		if (onCycleCreated && cycleAction === 'create') {
			onCycleCreated(createdCycle);
		} else {
			handleCancel();
		}
	};


	// Custom cycle create for wizard that passes the created cycle
	const wizardCycleCreate = (messageApi, cycleBody, successHandler, filters) => {
		dispatch(clear_cycles_errors('cycle_create'));
		dispatch({ type: 'CYCLE_CREATE_LOADING', payload: true });

		let url = process.env.REACT_APP_SERVER_URL + '/api/cycles/create';

		Axios.post(url, cycleBody)
		.then((res) => {
			messageApi.success(i18next.t('cycle.create.success'), 4);
			
			// Get the cycle ID from the response
			// Handle both cases: cycle ID as string or as object with _id
			const cycleId = typeof res.data.cycle === 'string' 
				? res.data.cycle 
				: res.data.cycle._id?.$oid || res.data.cycle._id || res.data.cycle.id;
			
			if (!cycleId) {
				// Fallback: just pass the basic cycle data
				dispatch({ type: 'CYCLE_CREATE_LOADING', payload: false });
				
				// Close the modal
				handleCancel();
				
				// Create a more complete cycle object from the form data
				const basicCycle = {
					_id: { $oid: res.data.cycle },
					name: cycleBody.name,
					description: cycleBody.description,
					area_type: cycleBody.area_type,
					scope: cycleBody.scope,
					start: cycleBody.start,
					end: cycleBody.end,
					status: 1, // Default to "Created" status
					creation_date: new Date().toISOString(),
					// Add organization info if available
					organization: cycleBody.organization,
					workspace: cycleBody.workspace,
					module: cycleBody.module,
					// Add area references if present
					region: cycleBody.region || null,
					zone: cycleBody.zone || null,
					depot: cycleBody.depot || null,
					group: cycleBody.group || null,
					route: cycleBody.route || null
				};
				successHandler(basicCycle);
				return;
			}

			// Load cycle into cache
			dispatch(load_single_cycle(cycleId));
			
			// Fetch complete cycle information directly
			let infoUrl = process.env.REACT_APP_SERVER_URL + `/api/cycles/${cycleId}/info`;
			
			Axios.get(infoUrl)
			.then((infoRes) => {
				dispatch({ type: 'CYCLE_CREATE_LOADING', payload: false });
				
				// Close the modal first
				handleCancel();
				
				// Pass the complete cycle info to the success handler
				successHandler(infoRes.data.cycle);
			})
			.catch((infoErr) => {
				// Fallback: just pass the basic cycle data
				dispatch({ type: 'CYCLE_CREATE_LOADING', payload: false });
				
				// Close the modal
				handleCancel();
				
				// Create a more complete cycle object from the form data
				const basicCycle = {
					_id: { $oid: cycleId },
					name: cycleBody.name,
					description: cycleBody.description,
					area_type: cycleBody.area_type,
					scope: cycleBody.scope,
					start: cycleBody.start,
					end: cycleBody.end,
					status: 1, // Default to "Created" status
					creation_date: new Date().toISOString(),
					// Add organization info if available
					organization: cycleBody.organization,
					workspace: cycleBody.workspace,
					module: cycleBody.module,
					// Add area references if present
					region: cycleBody.region || null,
					zone: cycleBody.zone || null,
					depot: cycleBody.depot || null,
					group: cycleBody.group || null,
					route: cycleBody.route || null
				};
				successHandler(basicCycle);
			});
		})
		.catch((err) => {
			dispatch({ type: 'CYCLE_CREATE_LOADING', payload: false });
			dispatch({ type: 'CYCLE_CREATE_ERROR', payload: err.response?.data });
			messageApi.error(i18next.t('cycle.create.error'), 4);
		});
	};

  // Function to disable dates before today
	const disabledDate = (current) => {
		const minDate = dayjs();
		return current && current < minDate.startOf('day');
	};

	const onValuesChange = (changedValues, allValues) => {
		// The end time validation depends on these fields, so it is checked again when they change
		if ((changedValues.date || changedValues.start_time) && useCustomTime && form.getFieldValue('end_time')) {
			form.validateFields(['end_time']);
		}

		if (changedValues.area_type) {
			changedValues.area_type === 1 ? setShowReferenceIput(false) : setShowReferenceIput(true);

			switch (changedValues.area_type) {
				case 2: // Region
					setAreaType(t('filters.region'));
					setReferenceOptions(create_select_options(regions.regions, 'name'));
					break;
				case 3: // Zone
					setAreaType(t('filters.zone'));
					setReferenceOptions(create_select_options(zones.zones, 'name'));
					break;
				case 4: // Depot
					setAreaType(t('filters.depot'));
					setReferenceOptions(create_select_options(depots.depots, 'name'));
					break;
				case 5: // Group
					setAreaType(t('filters.group'));
					setReferenceOptions(create_select_options(groups.groups, 'name'));
					break;
				case 6: // Route
					setAreaType(t('filters.route'));
					setReferenceOptions(create_select_options(routes.routes, 'name'));
					break;
				default:
					setReferenceOptions([]);
					break
			}
		}
	}

	const onOk = () => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };

		form
			.validateFields()
			.then((values) => {
				/*
					The times are always resolved on the organization timezone, even when the
					checkbox is not checked: a cycle of an organization on another timezone has to
					start and end on its own hours, not on the ones of the browser of the user
				*/
				const startTime = useCustomTime ? values.start_time : org_day_start();
				const endTime = useCustomTime ? values.end_time : org_day_end();

				const body = {
					organization: orgFilters.organization,
					workspace: orgFilters.workspace,
					module: orgFilters.module,
					area_type: 1,
					name: values.name,
					description: values.name,
					start: org_date_time_to_epoch(values.date[0], startTime, timezone),
					end: org_date_time_to_epoch(values.date[1], endTime, timezone),
					scope: 1,
					...(values.area_type === 2 ? { region: values.area } : { region: null }),
					...(values.area_type === 3 ? { zone: values.area } : { zone: null }),
					...(values.area_type === 4 ? { depot: values.area } : { depot: null }),
					...(values.area_type === 5 ? { group: values.area } : { group: null }),
					...(values.area_type === 6 ? { route: values.area } : { route: null }),
				}

				if (cycleAction === 'create') {
					// Create - use wizard-specific handler if onCycleCreated is provided
					if (onCycleCreated) {
						wizardCycleCreate(messageApi, body, handleWizardSuccess, filters);
					} else {
						dispatch(cycle_create(messageApi, body, handleCancel, filters));
					}
				} else if (cycleAction === 'update') {
					// Update
					dispatch(cycle_update(messageApi, cycleToUpdate?._id?.$oid, body, handleCancel, filters));
				} else if (cycleAction === 'extend') {
					// Extend
					const datesBody = { start: body.start, end: body.end };
					dispatch(cycle_extend(messageApi, cycleToUpdate?._id?.$oid, datesBody, handleCancel, filters));
				}
			})
			.catch((info) => {
				console.error(info);
			});
	}

	return (
		<Modal
			title={cycleAction === 'create' ? t('cycle.create.title') : cycleAction === 'update' ? t('cycle.edit.title') : t('cycle.extend.title')}
			centered
			open={isModalOpen}
			onCancel={handleCancel}
			maskClosable={false}
			width={700}
			footer={[
				<Button key='back' onClick={handleCancel}>
					{t('loadForm.cancel')}
				</Button>,
				<Button
					key='submit'
					type='primary'
					loading={cycleAction === 'create' ? cycle_create_loading : cycle_update_loading}
					// While the timezone is being resolved the cycle could be created with the wrong hours
					disabled={timezoneLoading}
					onClick={onOk}
				>
					{cycleAction === 'create' ? t('loadForm.create.title').split(' ')[0] : cycleAction === 'update' ? t('loadForm.edit.title').split(' ')[0] : t('cycle.extend.title').split(' ')[0]}
				</Button>,
			]}
		>
			<Form
				labelCol={{ span: 4 }}
				form={form}
				onValuesChange={onValuesChange}
			>
				<div style={{ marginBottom: '1.5rem' }}>
					<Alert
						type='info'
						showIcon
						message={
							timezoneLoading
								? t('cycle.customTime.timezoneLoading')
								: `${t('cycle.customTime.timezoneNote', { timezone: timezone })}${isFallbackTimezone ? ` ${t('cycle.customTime.timezoneFallback')}` : ''}`
						}
					/>
				</div>
				{cycles_errors.hasOwnProperty('cycle_create') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('cycle.create.error')} type='error' showIcon />
					</div>
				}
				{cycles_errors.hasOwnProperty('cycle_update') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('cycle.edit.error')} type='error' showIcon />
					</div>
				}
				{cycles_errors.hasOwnProperty('cycle_extend') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('cycle.extend.error')} type='error' showIcon />
					</div>
				}
				<Form.Item
					label={t('routesTable.headers.name')}
					name='name'
					rules={[
						{
							required: true,
							message: t('loadForm.validationMsg.name'),
						}
					]}
				>
					<Input disabled={cycleAction === 'extend'} placeholder={t('loadForm.placeholder.name')} />
				</Form.Item>
				{/* <Form.Item
					label={t('loadFileInput.description')}
					name='description'
					rules={[
						{
							required: true,
							message: t('loadForm.validationMsg.description'),
						}
					]}
				>
					<Input disabled={cycleAction === 'extend'} placeholder={t('loadForm.placeholder.description')} />
				</Form.Item>
				<Form.Item
					label={t('cycle.type')}
					name='area_type'
					rules={[
						{
							required: true,
							message: t('loadForm.validationMsg.type'),
						}
					]}
				>
					<Select
						showSearch
						placeholder={t('loadForm.placeholder.type')}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={areaTypesOptions}
						disabled={cycleAction === 'update' || cycleAction === 'extend'}
					/>
				</Form.Item>
				{showReferenceIput && (
					<Form.Item
						label={areaType}
						name='area'
						rules={[
							{
								required: true,
								message: t('cycle.validationMsg.reference'),
							}
						]}
					>
						<Select
							showSearch
							placeholder={t('cycle.placeholder.reference')}
							optionFilterProp='children'
							filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
							options={referenceOptions}
							disabled={cycleAction === 'update' || cycleAction === 'extend'}
						/>
					</Form.Item>
				)}
				<Form.Item
					label='Scope'
					name='scope'
					rules={[
						{
							required: true,
							message: t('schemasForm.validationMsg.scope'),
						}
					]}
				>
					<Select
						showSearch
						placeholder={t('schemasForm.placeholder.scope')}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={cycleScopeOptions}
						disabled={cycleAction === 'update' || cycleAction === 'extend'}
					/>
				</Form.Item> */}
				<Form.Item
					label={t('loadStatus.statusTable.date')}
					name='date'
					rules={[
						{
							required: true,
							message: t('cycle.validationMsg.date'),
						}
					]}
				>
					<DatePicker.RangePicker
						style={{ width: '100%' }}
						// disabledDate={disabledDate}
					/>
				</Form.Item>
				<Form.Item wrapperCol={{ offset: 4 }}>
					<Checkbox checked={useCustomTime} onChange={onCustomTimeChange}>
						{t('cycle.customTime.checkbox')}
					</Checkbox>
				</Form.Item>
				{useCustomTime && (
					<Fragment>
						<Form.Item label={t('cycle.customTime.format')}>
							<Radio.Group
								size='small'
								optionType='button'
								buttonStyle='solid'
								value={timeFormat}
								onChange={onTimeFormatChange}
							>
								<Radio.Button value={TIME_FORMAT_12H}>{t('cycle.customTime.format12h')}</Radio.Button>
								<Radio.Button value={TIME_FORMAT_24H}>{t('cycle.customTime.format24h')}</Radio.Button>
							</Radio.Group>
						</Form.Item>
						<Form.Item
							label={t('cycle.customTime.startTime')}
							name='start_time'
							rules={[
								{
									required: true,
									message: t('cycle.validationMsg.startTime'),
								}
							]}
						>
							<TimePicker
								style={{ width: '100%' }}
								use12Hours={is12Hours}
								format={timePickerFormat}
								allowClear={false}
								needConfirm={false}
							/>
						</Form.Item>
						<Form.Item
							label={t('cycle.customTime.endTime')}
							name='end_time'
							rules={[
								{
									required: true,
									message: t('cycle.validationMsg.endTime'),
								},
								{
									validator: validateEndTime
								}
							]}
						>
							<TimePicker
								style={{ width: '100%' }}
								use12Hours={is12Hours}
								format={timePickerFormat}
								allowClear={false}
								needConfirm={false}
							/>
						</Form.Item>
					</Fragment>
				)}
			</Form>
		</Modal>
	)
}

CyclesForm.propTypes = {
	messageApi: PropTypes.object,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	orgFilters: PropTypes.object.isRequired,
	cycleAction: PropTypes.string.isRequired,
	cycleToUpdate: PropTypes.object.isRequired,
	setCycleToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	onCycleCreated: PropTypes.func
}

export default CyclesForm;
